import {
  Button,
  Link as LinkScl,
  Modal,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
} from '@ace-clublabs/shared-component-library';
import { useMutation } from '@tanstack/react-query';
import { atcb_action } from 'add-to-calendar-button-react';
import dayjs from 'dayjs';
import { useEffect, useId, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import ErrorPage from '@/components/shared/ErrorPage';
import { constants } from '@/constants';
import useContactHashes from '@/hooks/useContactHashes';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useResetTimeslotOnModify from '@/hooks/useResetTimeslotOnModify';
import { queryClient } from '@/lib/queryClient';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelector,
} from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import useThemeStore from '@/store/ThemeStore';
import { patchCancel } from '../api/patchCancel';
import AppointmentInformation from '../components/AppointmentInformation';
import AppointmentInformationSkeletons from '../components/AppointmentInformationSkeletons';
import CancelAppointmentModalBody from '../components/CancelAppointmentModalBody';
import useAppointmentMutationStatus from '../hooks/useAppointmentMutationStatus';
import { getEmailOption } from '../utils/getEmailOption';
import type {
  ModifyAppointmentRequestData,
  ModifyAppointmentResponseData,
} from '../utils/ModifyAppointmentRequestData';

interface EmailEvent {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: string;
  options: Array<
    | 'Google'
    | 'Apple'
    | 'iCal'
    | 'Microsoft365'
    | 'MicrosoftTeams'
    | 'Outlook.com'
    | 'Yahoo'
  >;
  timeZone: string;
  useUserTZ?: boolean;
}

function Confirmation() {
  useLandingPageRedirection();

  const { isLoading, isError, errorSource } = useAppointmentMutationStatus();

  useBlocker(({ currentLocation, nextLocation }) => {
    const canNavigateToAgentSelection = isError && errorSource === 'reschedule';

    const allowedPaths = ['date-selection', 'cancel-appointment'];

    if (canNavigateToAgentSelection) {
      allowedPaths.push('agent-selection');
    }

    return (
      currentLocation.pathname !== nextLocation.pathname &&
      !allowedPaths.some((path) => nextLocation.pathname.includes(path))
    );
  });

  const _modalTitleId = useId();
  const _modalContentId = useId();

  const [isCancellingAppointment, setIsCancellingAppointment] = useState(false);
  const hasTagged = useRef(false);

  const {
    appointmentId,
    confirmationId,
    customerId,
    contactInfo,
    selectedDestination,
    searchedDestination,
    confirmedAppointment,
    selectedBranch,
    userSearch,
  } = useAppointmentStore();

  const { resetTimeslot } = useResetTimeslotOnModify();

  const { sessionId, sessionSource, flowType, setFlowType } = useSessionStore();

  const { emailHash, phoneHash, contactInfoTaggingReady } = useContactHashes();

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelector,
  );

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [appointmentCancelledFailed, setAppointmentCancelledFailed] =
    useState(false);
  const handleCloseModal = () => {
    setOpenCancelModal(false);
  };

  const navigate = useNavigate();

  const { isLuxuryTheme } = useThemeStore();

  const tagging = useTagging();

  const cancelAppointment = useMutation({
    mutationFn: (cancelAppointmentData: ModifyAppointmentRequestData) =>
      patchCancel(cancelAppointmentData),
    onSuccess: (data: ModifyAppointmentResponseData) => {
      setIsCancellingAppointment(false);
      navigate(`/cancel-appointment/${data.appointmentId}`);
    },
    onError: (err) => {
      if (err.message !== constants.TANSTACK_ABORTED_ERROR)
        setAppointmentCancelledFailed(true);
    },
  });

  const cancelAppointmentHandler = () => {
    setIsCancellingAppointment(true);
    setFlowType('cancel');
    const cancelAppointmentData: ModifyAppointmentRequestData = {
      agentResourceId: confirmedAppointment?.agentInfo?.resourceId ?? '',
      appointmentId: appointmentId ?? '',
      status: 'Canceled',
    };
    cancelAppointment.mutate(cancelAppointmentData);
  };

  useEffect(() => {
    if (flowType === 'cancel') {
      return;
    }
    if (!contactInfoTaggingReady) return;
    if (!confirmationId) return;

    if (!hasTagged.current) {
      const category = isLuxuryTheme
        ? 'travel:luxury scheduler'
        : 'travel:scheduler';

      const confirmationTags = {
        category: [category],
        confirmation_id: confirmationId,
        product: ['appointment'],
        quantity: ['1'],
        custID: customerId,
      };

      // if the user lands on the confirmation page through the email link, don't fire the confirmation tags OR
      // if they end up modifying the appointment even through email, we want to fire the confirmation tags
      const shouldPopulateConfirmationTags =
        sessionSource !== 'email' ||
        (sessionSource === 'email' && flowType === 'modify');

      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;

      const commPref = isInBranchAppointment ? 'in person' : 'phone';

      const contactType = contactInfo.communicationPreference?.toLowerCase();

      tagging.tag('view', sessionSource, {
        page: Page.confirmation,
        sessionid: sessionId,
        flow: flowType,
        trv_end_location: trvEndLocation,
        comm_pref: commPref,
        contact_type: contactType,
        hd_schdlr_1: emailHash,
        hd_schdlr_2: phoneHash,
        // for reporting reasons, we don't want these confirmation tags to be added if the source is email
        ...(shouldPopulateConfirmationTags ? confirmationTags : {}),
      });

      hasTagged.current = true;
    }
  }, [
    confirmationId,
    contactInfo.communicationPreference,
    emailHash,
    phoneHash,
    contactInfoTaggingReady,
    customerId,
    flowType,
    searchedDestination,
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    isLuxuryTheme,
    userSearch,
    isInBranchAppointment,
  ]);

  const clickAddToCalendarHandler = () => {
    const selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const baseDate = dayjs(
      confirmedAppointment?.selectedDate ||
        // biome-ignore lint/style/noNonNullAssertion lint/suspicious/noNonNullAssertedOptionalChain: selectedStartTime is always set when this handler runs
        new Date(confirmedAppointment?.selectedStartTime!),
    );

    // Determine location based on appointment type
    const location =
      isInBranchAppointment && selectedBranch
        ? selectedBranch.branchAddress
        : 'Online';

    // Update description based on appointment type
    const branchName = selectedBranch?.branchName
      ? `${selectedBranch.branchName} branch`
      : 'branch';
    const destinationText = searchedDestination || selectedDestination?.name;
    const description = isInBranchAppointment
      ? `We look forward to seeing you at the ${branchName} on ${baseDate.format('MMMM D')} at ${dayjs(confirmedAppointment?.selectedStartTime).format('h:mma')} for our appointment about ${destinationText}!`
      : `We'll give you a call on ${baseDate.format('MMMM D')} at ${dayjs(confirmedAppointment?.selectedStartTime).format('h:mma')} to chat about ${destinationText}!`;

    const emailEvent: EmailEvent = {
      name: `Your appointment with ${confirmedAppointment?.agentInfo?.name}`,
      description,
      startDate: baseDate.format('YYYY-MM-DD'),
      startTime: dayjs(confirmedAppointment?.selectedStartTime).format('HH:mm'),
      endDate: dayjs(confirmedAppointment?.selectedEndTime).format(
        'YYYY-MM-DD',
      ),
      endTime: dayjs(confirmedAppointment?.selectedEndTime).format('HH:mm'),
      location,
      useUserTZ: false,
      options: [contactInfo.email ? getEmailOption(contactInfo.email) : 'iCal'],
      timeZone: selectedTimezone,
    };

    atcb_action(emailEvent);
  };

  const handleModifyAppointment = () => {
    queryClient.resetQueries({
      queryKey: ['timeSlots'],
    });

    queryClient.resetQueries({
      queryKey: ['nearbyAgents'],
    });

    resetTimeslot();
    navigate(`/reschedule-appointment/${appointmentId}/date-selection`);
  };

  if (appointmentCancelledFailed) {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <AppointmentInformationSkeletons isInBranch={isInBranchAppointment} />
    );
  }

  if (isError) {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          if (errorSource === 'reschedule') {
            navigate(-1);
            return;
          }

          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
      />
    );
  }

  return (
    <>
      <AppointmentInformation
        onClickModifyAppointment={handleModifyAppointment}
        onClickCancelAppointment={() => setOpenCancelModal(true)}
        buttonGroup={{
          'data-testid': 'addToCalendarOrCloseButtonGroup',
          children: (
            <>
              <Button
                label='Add to calendar'
                onClick={clickAddToCalendarHandler}
                fullWidth
                variant='outlined'
                sx={{
                  backgroundColor: (theme) => theme.palette.common.white,
                }}
              />
              <LinkScl
                href={constants.TRAVEL_HOME_PAGE_URL}
                sx={{ width: '100%' }}
                data-testid='closePageButtonLink'
              >
                <Button label='Close page' fullWidth />
              </LinkScl>
            </>
          ),
        }}
      />
      <Modal
        open={openCancelModal}
        onClose={handleCloseModal}
        size='medium'
        sx={{
          '& .MuiPaper-root': {
            padding: 0,
          },
          '& .MuiDialog-paper': {
            minHeight: 'auto',
          },
        }}
        data-testid={'modal'}
      >
        <ModalCloseIconButton aria-label='Close' onClick={handleCloseModal} />
        <ModalTitle
          id={_modalTitleId}
          sx={[
            { padding: '1.5rem', marginBottom: 0, pr: '3rem' },
            isLuxuryTheme && { color: 'secondary.400' },
          ]}
        >
          Are you sure you want to cancel your appointment?
        </ModalTitle>
        <ModalContent id={_modalContentId}>
          <CancelAppointmentModalBody
            onClickClose={handleCloseModal}
            onClickCancel={cancelAppointmentHandler}
            isCancellingAppointment={isCancellingAppointment}
          />
        </ModalContent>
      </Modal>
    </>
  );
}

export default Confirmation;
